import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import AuthLayout from "../../components/component/AuthLayout";
import { usePublicSignUp } from "../../queries/auth";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

interface SignupFormValues {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
}

const validationSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Please confirm your password"),
  organizationName: Yup.string()
    .trim()
    .required("Organization name is required"),
});

const SignupPage = () => {
  const navigate = useNavigate();
  const { mutateAsync: publicSignUpMutation, isPending } = usePublicSignUp();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const initialValues: SignupFormValues = {
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    organizationName: "",
  };

  const handleSubmit = async (
    values: SignupFormValues,
    { setSubmitting, resetForm }: any,
  ) => {
    const signupData = {
      name: values.username,
      email: values.email,
      password: values.password,
      organizationName: values.organizationName,
    };

    try {
      await publicSignUpMutation(signupData, {
        onSuccess: () => {
          navigate("/verify-email", { state: { email: values.email } });
          toast.success(
            "Account created! Check your email for the verification code.",
            { position: "bottom-right", autoClose: 3000 },
          );
          resetForm();
        },
      });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message ||
          "Signup failed. Please check your information and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout showLogo={false}>
      <div>
        {/* Login / Sign Up Tabs */}
        <div className="flex p-1 mb-5 bg-gray-100 rounded-lg">
          <Link
            to="/login"
            className="flex-1 py-2 text-sm font-semibold text-center rounded-md text-gray-500 hover:text-gray-700 transition-all"
          >
            Login
          </Link>
          <button
            type="button"
            className="flex-1 py-2 text-sm font-semibold text-center rounded-md bg-primary text-white shadow-sm transition-all"
          >
            Sign Up
          </button>
        </div>

        <h1 className="text-2xl font-bold mb-1">Create Account</h1>
        <p className="text-gray-500 text-sm mb-5">Fill in your details to get started</p>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-3.5">
              <div>
                <label
                  htmlFor="username"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Username <span className="text-red-500">*</span>
                </label>
                <Field
                  id="username"
                  name="username"
                  type="text"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
                  placeholder="Enter your username"
                />
                <ErrorMessage
                  name="username"
                  component="p"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
                  placeholder="you@example.com"
                />
                <ErrorMessage
                  name="email"
                  component="p"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Field
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="w-full px-3 py-2.5 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="password"
                  component="p"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Field
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    className="w-full px-3 py-2.5 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <ErrorMessage
                  name="confirmPassword"
                  component="p"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="organizationName"
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <Field
                  id="organizationName"
                  name="organizationName"
                  type="text"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white transition-shadow"
                  placeholder="Enter your organization name"
                />
                <ErrorMessage
                  name="organizationName"
                  component="p"
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white font-semibold py-2.5 px-4 rounded-lg hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || isPending}
              >
                {isSubmitting || isPending
                  ? "Creating Account..."
                  : "Create Account & Organization"}
              </button>

            </Form>
          )}
        </Formik>
      </div>
      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </AuthLayout>
  );
};

export default SignupPage;
